import { createSlice } from '@reduxjs/toolkit';

import { getOpenHours } from 'util';
import { weekdays } from 'util/constants';

const recurringAvailability = createSlice({
  name: 'recurringAvailability',
  initialState: {
    isLoading: false,
    recurring: [],
    slotsToDelete: [],
    unmodifiedAvailabilityIds: [],
  },
  reducers: {
    availabilitySuccess(state, { payload }) {
      const recurring = payload;
      const recurrentIds =
        recurring.length === 0
          ? []
          : recurring
              .map((el) =>
                el.time
                  .map((time) => {
                    if (time.isPlaceholder) {
                      return null;
                    }
                    return time.uuid;
                  })
                  .filter(Boolean)
              )
              .flat();
      return {
        ...state,
        isLoading: false,
        recurring,
      };
    },
    activateRecurringDay(state, { payload }) {
      const { day, openHours, increment } = payload;
      const openHoursData =
        openHours.filter((el) => el.dow.value === day)[0] || {};
      const openingTimes = getOpenHours(openHoursData.time, increment / 60);
      const dowId = openHoursData.dow.id;

      const times = openingTimes
        .map((obj, i) => {
          if (!openingTimes[i + 1]) return null;
          return {
            start: obj.valueFormatted,
            end: openingTimes[i + 1].valueFormatted,
            uuid: crypto.randomUUID(),
            isPlaceholder: true,
          };
        })
        .filter(Boolean);

      state.recurring = [
        ...state.recurring,
        {
          dow: { id: dowId, value: day },
          time: times,
        },
      ];
    },
    addRecurringPlaceholder(state, { payload }) {
      const dowObj = state.recurring.filter(
        (obj) => obj.dow.value === payload.dow
      )?.[0];
      const dowIndex = state.recurring.indexOf(dowObj);

      if (dowIndex >= 0) {
        state.recurring[dowIndex].time = [
          ...state.recurring[dowIndex].time,
          {
            start: '',
            end: '',
            uuid: crypto.randomUUID(),
            isPlaceholder: true,
          },
        ];
      } else {
        state.recurring = [
          ...state.recurring,
          {
            dow: { value: payload.dow, id: weekdays.indexOf(payload.dow) + 1 },
            time: [
              {
                start: '',
                end: '',
                uuid: crypto.randomUUID(),
                isPlaceholder: true,
              },
            ],
          },
        ];
      }
    },
    editRecurringTime(state, { payload }) {
      const { day, range, obj } = payload;

      // Find day object
      const dowObj = state.recurring.filter(
        (obj) => obj.dow.value === day
      )?.[0];
      const dowIndex = state.recurring.indexOf(dowObj);

      // Find range object inside day object
      const timeObj = state.recurring[dowIndex].time.filter(
        (el) => el.uuid === range.uuid
      )[0];
      const timeIndex = state.recurring[dowIndex].time.indexOf(timeObj);

      state.recurring[dowIndex].time[timeIndex] = {
        ...state.recurring[dowIndex].time[timeIndex],
        ...obj,
      };
    },
    removeRecurringTime(state, { payload }) {
      const { day, uuid, isPlaceholder } = payload;
      // Find day object
      const dowObj = state.recurring.filter(
        (obj) => obj.dow.value === day
      )?.[0];
      const dowIndex = state.recurring.indexOf(dowObj);

      const updatedAvailability = state.recurring[dowIndex].time.filter(
        (el) => el.uuid !== uuid
      );
      if (updatedAvailability.length === 0) {
        state.recurring = state.recurring.filter(
          (el) => el.dow.value !== payload.day
        );
      } else {
        state.recurring[dowIndex].time = updatedAvailability;
      }
      if (!isPlaceholder) {
        // If it's a real slot we'll delete it later
        state.slotsToDelete = [...state.slotsToDelete, { uuid }];
      }
    },
    removeRecurringDay(state, { payload }) {
      // Find day object
      const dowObj = state.recurring.filter(
        (obj) => obj.dow.value === payload.dow
      )?.[0];

      const nonPlaceholderRanges = dowObj.time.filter(
        (el) => !el.isPlaceholder
      );
      const rangesToDelete = nonPlaceholderRanges.map((el) => ({
        uuid: el.uuid,
      }));

      const updatedAvailability = state.recurring.filter(
        (el) => el.dow.value !== payload.dow
      );
      state.recurring = updatedAvailability;

      // If it's a real slot we'll delete it later
      state.slotsToDelete = [...state.slotsToDelete, ...rangesToDelete];
    },
  },
});

const { actions, reducer } = recurringAvailability;

export const {
  availabilityRequest,
  availabilitySuccess,
  availabilityFailure,
  activateRecurringDay,
  removeRecurringRange,
  addRecurringPlaceholder,
  editRecurringTime,
  removeRecurringTime,
  removeRecurringDay,
} = actions;

export default reducer;
